import { ref, uploadBytes } from "firebase/storage";
import { toLower, uniq, orderBy as lodashOrderBy } from "lodash";
import { v4 as uuid } from 'uuid';
import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberEmpty, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isNumberNotEmpty, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { convertToSlug } from "../utils/stringFormatter";
import { addValueToChildArray, create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { storage, db } from "../firebase";
import ShippingClass from "./ShippingClass";
import ProductDimentions from "./ProductDimensions";
import { asTimeZone } from "../utils/dateFormatter";
import moment from "moment-timezone";
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from "firebase/firestore";
import { normalizeSurveyFlowItems, validateUniqueSurveyFlowKeys } from "./SurveyFlowItem";

const normalizeIngredientIds = (...ingredientLists) => {
    return Array.from(new Set(ingredientLists.flatMap((ingredientList) => {
        if (Array.isArray(ingredientList)) {
            return ingredientList;
        }
        return isStringNotEmpty(ingredientList) ? [ingredientList] : [];
    }).map((ingredient) => {
        const value = typeof ingredient === 'string'
            ? ingredient
            : (ingredient?.id ?? ingredient?.value ?? ingredient?.label ?? ingredient?.name ?? '');
        return String(value).trim().toUpperCase().replace(/[\s-]+/g, '_');
    }).filter((ingredientId) => isStringNotEmpty(ingredientId))));
};

class Product {
    static collectionName = 'products';

    id = null;
    name = null;
    slug = convertToSlug(this.name);
    visibleToCustomers = false;
    categoryIds = []; // array of productCategoryId
    attributes = []; // array of product attributes
    description = null;
    dimensions = new ProductDimentions().toDto();
    weight = 0.0;
    featured = false;
    #images = [];
    imageRefIds = [];
    metaData = []; // array of product meta data
    regularPrice = 0.0; // per unit price
    salePrice = null; // optional and will override the regular price
    compareAtPrice = null; // optional and will show a strike through as if the product is on discount
    purchasable = false;
    sku = null;
    shortSku = null;
    productFamilyId = null;
    offerGroupId = null;
    commercialEquivalencyGroupId = null;
    formulationCode = null;
    activeIngredientIds = []; // active ingredient IDs used for clinical safety checks
    additiveIds = []; // additive IDs used for clinical safety checks
    soldIndividually = false; // Can the product only be sold on its own and only a quantity of 1?
    status = "PUBLISHED"; // Enum ProductStatuses
    manageStock = false;
    lowStockAmount = 0;
    stockStatus = "IN_STOCK"; // Enum StockStatuses
    stockQuantity = 0;
    taxStatus = "TAXABLE"; // Enum TaxStatuses
    tags = []; // array of strings
    productType = "SIMPLE"; // Enum ProductTypes
    displayOrder = 0;
    isVirtual = false; // is the product a service with no physical nature
    isSale = false; // is the product on sale, use salePrice instead if true
    brand = null;
    gtin = null;
    msrp = null;
    shippingClassId = null;
    shippingRequired = false;

    duration = 0; // added 12/5/2023 for services such as telehealth visits in minutes

    requiresConfirmation = false; // requires patient confirmation - needs to be set to true if there are surveys and they need to be filled out for each order
    requiresClinicianApproval = false; // requires that the doctor prescribes/approves the product
    requiresClinicianEncounter = false; // requires an in-person or telehealth dr appt
    requiresSurveys = false; // requires patient to fill out forms/surveys

    // require documents to be provided when ordering product
    requiredDocuments = []; // array of object { documentType, requirement, scope } requirement: enum DocumentRequirements, scope: enum SurveyScopes

    surveys = []; // array of SurveyFlowItem overrides

    acceptsInsurance = false;
    productRules = []; // array of ProductRule
    addOnRules = []; // array of ProductAddOnRule - defines which add-on products are automatically included when this product is ordered

    count = 1;
    countUnits = null;
    packaging = null;

    // FOR STACKED PRODUCTS
    stackedOptions = []; // { productCategoryId, (productId, variantId), isRequired } if user disqualifies for an option that is required, then they are disqualified from the stacked product as a whole.
    stackEncounters = false; // if encounters are stacked then all visits are in sync for the products included in the stack. if false then the encounters can happen separately per product in the stack
    stackShipments = false; // if shipments are stacked then all products are shipped together

    relatedProductIds = []; // could have other products that have this product in them and the list can be further filtered by the customer to not show products that theyve already purchased or they have disqualified from
    crossSellProductIds = []; // you would only show products that could be purchased with this product so you wouldn't show a stack or singular product that has this product in it, could further filter out products that have already been purchased

    // default shipping method for the pharmacy fulfilling the order
    carrier = null;
    carrierShippingMethod = null;
    shipRefrigerated = false;

    conditionIds = [];
    biomarkers = []; // array of ProductBiomarkers (renamed from metrics)

    // FOR SUBSCRIPTION PRODUCTS
    subscriptionPrice = 0.0; // Price per renewal if customer opts in to receive product on a subscription rather than one time purchase
    subscriptionPeriod = "MONTH"; // enum SubscriptionBillingPeriods // the unit of time for the recurring billing schedule, e.g. week, month or year.
    subscriptionPeriodInterval = 1; // The number of intervals between each renewal payment for recurring billing schedule, e.g. 2 for every 2 months.
    subscriptionLength = 0; // The number of times the subscription will renew. For subscriptions that do not expire, specify 0.
    subscriptionLimit = "NONE"; // Limit customers to only one subscription of a certain product. Can be limited to one active subscription, any subscription or not limited. Enum SubscriptionLimits
    subscriptionSyncRenewalsDate = null; // Day of the week (Sun = 1, Mon = 0, Tue = 1, etc.), of the month (1 – 28), or a specific calendar day each year (e.g. Dec 8 = {'day':8,'month':12}). Note: synchronized subscriptions for day of the month can only be renewed on days 1 – 27, or on the last day of the month (28).
    subscriptionSyncRenewals = false; // Boolean; Sync renewals to a specific day of the month after the first payment
    subscriptionOneTimeShipping = false; //	Only charge shipping once on the initial order. 
    subscriptionSignUpFee = 0.0; // The cost of the sign up fee for the subscription.

    // FOR TEST PRODUCTS
    labId = null; // default coveredEntityId
    waitTime = 0; // time in minutes to wait before at-home results will be ready (applicable to tests with hasAtHomeResults = true)
    hasAtHomeResults = false;
    specimenType = null; // enum TestSpecimens - BLOOD, URINE, SALIVA, etc.

    // FOR LAB TEST SUBSCRIPTION PRODUCTS (productType: TEST_SUBSCRIPTION)
    panelIds = []; // labPanel doc IDs composing the full program
    biomarkerCount = 0; // denormalized total unique biomarker count
    collectionMethod = null; // enum CollectionMethods: WALK_IN, AT_HOME_TEST_KIT, AT_HOME_PHLEBOTOMY
    requiresFasting = false;
    fastingInstructions = null;
    testSchedule = []; // TestScheduleEntry[]

    // FOR TREATMENT PRODUCTS - quarterly monitoring panels needed when patient is on this treatment
    monitoringPanelIds = [];

    detailSections = []; // array of ProductMetaData - for ShippingPolicy, RefundPolicy, Specifications, etc.

    // pay up front may be for 1 year at a time, 6 months at a time, 3 months at a time, etc.
    bundleDiscounts = []; // array of object { couponCode, bundlePaymentCount }

    bundleShipmentCount = 1; // maximum number of orders that can be shipped together by the default pharmacy

    clinicianEncounterType = 'NONE';
    clinicianEncounterFrequency = 1;
    clinicianEncounterPrice = 0.0; // if there is a separate fee for the encounter
    ordersPerClinicianEncounter = 1; // per clinician encounter, how many renewal orders should be generated (can be post dated to be filled later)

    productSchedules = []; // array of ProductSchedule which will determine which product variants and quantities will be included in each order

    practiceId = null; // coveredEntityId of the physician practice providing the encounters
    pharmacyId = null; // coveredEntityId of the pharmacy fulfilling the order

    defaultCampaignId = null;

    variants = []; // array of Variant

    // for medications
    instructions = null;
    strength = null;
    form = null; // form of the medication (e.g., tablet, liquid)
    route = null; // route of administration (e.g., oral, intravenous)
    isControlled = false; // indicates if the product is a controlled substance medication

    constructor(product) {
        if (isObjectNotEmpty(product)) {
            if (isStringNotEmpty(product.id)) { this.id = product.id; }
            if (isStringNotEmpty(product.name)) { this.name = product.name; }
            if (isStringNotEmpty(product.slug)) { this.slug = product.slug; }
            else { this.slug = convertToSlug(this.name); }

            if (isBooleanTrue(product.visibleToCustomers)) { this.visibleToCustomers = true; }
            if (isListNotEmpty(product.categoryIds)) { this.categoryIds = product.categoryIds; }
            if (isListNotEmpty(product.attributes)) { this.attributes = product.attributes; }
            if (isStringNotEmpty(product.description)) { this.description = product.description; }
            if (isNumberNotEmpty(product.width) && isNumberNotEmpty(product.height) && isNumberNotEmpty(product.length)) { this.dimensions = new ProductDimentions(product.width, product.height, product.length).toDto(); }
            if (isNumberGreaterThanOrEqualToZero(product.weight)) { this.weight = product.weight; }
            if (isBooleanTrue(product.featured)) { this.featured = true; }
            if (isListNotEmpty(product.images)) { this.#images = product.images; }
            if (isListNotEmpty(product.imageRefIds)) { this.imageRefIds = product.imageRefIds; }
            if (isListNotEmpty(product.metaData)) { this.metaData = product.metaData; }
            if (isNumberGreaterThanOrEqualToZero(product.regularPrice)) { this.regularPrice = product.regularPrice; }
            if (isNumberGreaterThanOrEqualToZero(product.salePrice)) { this.salePrice = product.salePrice; }
            if (isNumberGreaterThanOrEqualToZero(product.compareAtPrice)) { this.compareAtPrice = product.compareAtPrice; }
            if (isBooleanTrue(product.purchasable)) { this.purchasable = true; }
            if (isStringNotEmpty(product.sku)) { this.sku = product.sku; }
            if (isStringNotEmpty(product.shortSku)) { this.shortSku = product.shortSku; }
            if (isStringNotEmpty(product.productFamilyId)) { this.productFamilyId = product.productFamilyId; }
            if (isStringNotEmpty(product.offerGroupId)) { this.offerGroupId = product.offerGroupId; }
            if (isStringNotEmpty(product.commercialEquivalencyGroupId)) { this.commercialEquivalencyGroupId = product.commercialEquivalencyGroupId; }
            if (isStringNotEmpty(product.formulationCode)) { this.formulationCode = product.formulationCode; }
            const activeIngredientIds = normalizeIngredientIds(product.activeIngredientIds);
            if (isListNotEmpty(activeIngredientIds)) { this.activeIngredientIds = activeIngredientIds; }
            const additiveIds = normalizeIngredientIds(product.additiveIds, product.additiveCodes, product.additives);
            if (isListNotEmpty(additiveIds)) { this.additiveIds = additiveIds; }
            if (isBooleanTrue(product.soldIndividually)) { this.soldIndividually = true; }
            if (isStringNotEmpty(product.status)) { this.status = product.status; }
            if (isStringNotEmpty(product.stockStatus)) { this.stockStatus = product.stockStatus; }
            if (isNumberGreaterThanOrEqualToZero(product.stockQuantity)) { this.stockQuantity = product.stockQuantity; }
            if (isNumberGreaterThanOrEqualToZero(product.lowStockAmount)) { this.lowStockAmount = product.lowStockAmount; }
            if (isStringNotEmpty(product.taxStatus)) { this.taxStatus = product.taxStatus; }
            if (isListNotEmpty(product.tags)) { this.tags = product.tags; }
            if (isStringNotEmpty(product.productType)) { this.productType = product.productType; }
            if (isNumberGreaterThanOrEqualToZero(product.displayOrder)) { this.displayOrder = product.displayOrder; }
            if (isBooleanTrue(product.isVirtual)) { this.isVirtual = true; }
            if (isBooleanTrue(product.isSale)) { this.isSale = true; }
            if (isBooleanTrue(product.manageStock)) { this.manageStock = true; }
            if (isNumberGreaterThanOrEqualToZero(product.msrp)) { this.msrp = product.msrp; }
            if (isStringNotEmpty(product.brand)) { this.brand = product.brand; }
            if (isStringNotEmpty(product.gtin)) { this.gtin = product.gtin; }
            if (isStringNotEmpty(product.shippingClassId)) { this.shippingClassId = product.shippingClassId; }
            if (isBooleanTrue(product.shippingRequired)) { this.shippingRequired = true; }

            if (isNumberGreaterThanOrEqualToZero(product.duration)) { this.duration = product.duration; }

            if (isBooleanTrue(product.requiresConfirmation)) { this.requiresConfirmation = true; }
            if (isBooleanTrue(product.requiresClinicianApproval)) { this.requiresClinicianApproval = true; }
            if (isBooleanTrue(product.requiresClinicianEncounter)) { this.requiresClinicianEncounter = true; }
            if (isBooleanTrue(product.requiresSurveys)) { this.requiresSurveys = true; }

            if (isListNotEmpty(product.requiredDocuments)) { this.requiredDocuments = product.requiredDocuments; }

            if (isListNotEmpty(product.surveys)) { this.surveys = normalizeSurveyFlowItems(product.surveys); }

            if (isListNotEmpty(product.conditionIds)) { this.conditionIds = product.conditionIds; }
            if (isListNotEmpty(product.biomarkers)) { this.biomarkers = product.biomarkers; }

            if (isNumberGreaterThanOrEqualToZero(product.subscriptionPrice)) { this.subscriptionPrice = product.subscriptionPrice; }
            if (isStringNotEmpty(product.subscriptionPeriod)) { this.subscriptionPeriod = product.subscriptionPeriod; }
            if (isNumberGreaterThanOrEqualToZero(product.subscriptionPeriodInterval)) { this.subscriptionPeriodInterval = product.subscriptionPeriodInterval; }
            if (isNumberGreaterThanOrEqualToZero(product.subscriptionLength)) { this.subscriptionLength = product.subscriptionLength; }
            if (isStringNotEmpty(product.subscriptionLimit)) { this.subscriptionLimit = product.subscriptionLimit; }
            if (isBooleanTrue(product.subscriptionOneTimeShipping)) { this.subscriptionOneTimeShipping = true; }
            if (isNumberGreaterThanOrEqualToZero(product.subscriptionSignUpFee)) { this.subscriptionSignUpFee = product.subscriptionSignUpFee; }

            if (isBooleanTrue(product.subscriptionSyncRenewals)) {
                this.subscriptionSyncRenewals = true;

                if (this.subscriptionPeriod === "WEEK" || this.subscriptionPeriod === "MONTH") {
                    if (isNumberGreaterThanOrEqualToZero(product.subscriptionSyncRenewalsDate)) { this.subscriptionSyncRenewalsDate = product.subscriptionSyncRenewalsDate; }
                } else if (this.subscriptionPeriod === "YEAR") {
                    if (isObjectNotEmpty(product.subscriptionSyncRenewalsDate)
                        && product.subscriptionSyncRenewalsDate.hasOwnProperty('day')
                        && product.subscriptionSyncRenewalsDate.hasOwnProperty('month')
                        && isNumberGreaterThanZero(product.subscriptionSyncRenewalsDate.day)
                        && isNumberGreaterThanZero(product.subscriptionSyncRenewalsDate.month)) {
                        this.subscriptionSyncRenewalsDate = product.subscriptionSyncRenewalsDate;
                    }
                }
            }

            if (isStringNotEmpty(product.labId)) { this.labId = product.labId; }
            if (isNumberGreaterThanOrEqualToZero(product.waitTime)) { this.waitTime = product.waitTime; }
            if (isBooleanTrue(product.hasAtHomeResults)) { this.hasAtHomeResults = true; }
            if (isStringNotEmpty(product.specimenType)) { this.specimenType = product.specimenType; }

            // Lab test subscription fields
            if (isListNotEmpty(product.panelIds)) { this.panelIds = product.panelIds; }
            if (isNumberGreaterThanOrEqualToZero(product.biomarkerCount)) { this.biomarkerCount = product.biomarkerCount; }
            if (isStringNotEmpty(product.collectionMethod)) { this.collectionMethod = product.collectionMethod; }
            if (isBooleanTrue(product.requiresFasting)) { this.requiresFasting = true; }
            if (isStringNotEmpty(product.fastingInstructions)) { this.fastingInstructions = product.fastingInstructions; }
            if (isListNotEmpty(product.testSchedule)) { this.testSchedule = product.testSchedule; }

            // Treatment monitoring panels
            if (isListNotEmpty(product.monitoringPanelIds)) { this.monitoringPanelIds = product.monitoringPanelIds; }

            if (isBooleanTrue(product.acceptsInsurance)) { this.acceptsInsurance = true; }
            if (isObjectNotEmpty(product.productRules)) { this.productRules = product.productRules; }
            if (isListNotEmpty(product.addOnRules)) { this.addOnRules = product.addOnRules; }

            if (isNumberNotEmpty(product.count)) { this.count = product.count; }
            if (isStringNotEmpty(product.countUnits)) { this.countUnits = product.countUnits; }
            if (isStringNotEmpty(product.packaging)) { this.packaging = product.packaging; }

            if (isListNotEmpty(product.detailSections)) { this.detailSections = product.detailSections; }

            if (isListNotEmpty(product.bundleDiscounts)) { this.bundleDiscounts = product.bundleDiscounts; }

            if (isListNotEmpty(product.stackedOptions)) { this.stackedOptions = product.stackedOptions; }
            if (isBooleanTrue(product.stackEncounters)) { this.stackEncounters = true; }
            if (isBooleanTrue(product.stackShipments)) { this.stackShipments = true; }

            if (isListNotEmpty(product.relatedProductIds)) { this.relatedProductIds = product.relatedProductIds; }
            if (isListNotEmpty(product.crossSellProductIds)) { this.crossSellProductIds = product.crossSellProductIds; }

            if (isStringNotEmpty(product.carrier)) { this.carrier = product.carrier; }
            if (isStringNotEmpty(product.carrierShippingMethod)) { this.carrierShippingMethod = product.carrierShippingMethod; }
            if (isBooleanTrue(product.shipRefrigerated)) { this.shipRefrigerated = true; }

            if (isNumberGreaterThanOrEqualToZero(product.bundleShipmentCount)) { this.bundleShipmentCount = product.bundleShipmentCount; }

            if (isStringNotEmpty(product.clinicianEncounterType)) { this.clinicianEncounterType = product.clinicianEncounterType; }
            if (isNumberGreaterThanOrEqualToZero(product.clinicianEncounterFrequency)) { this.clinicianEncounterFrequency = product.clinicianEncounterFrequency; }
            if (isNumberGreaterThanOrEqualToZero(product.clinicianEncounterPrice)) { this.clinicianEncounterPrice = product.clinicianEncounterPrice; }
            if (isNumberGreaterThanZero(product.ordersPerClinicianEncounter)) { this.ordersPerClinicianEncounter = product.ordersPerClinicianEncounter; }

            if (isListNotEmpty(product.productSchedules)) { this.productSchedules = product.productSchedules; }

            if (isStringNotEmpty(product.practiceId)) { this.practiceId = product.practiceId; }
            if (isStringNotEmpty(product.pharmacyId)) { this.pharmacyId = product.pharmacyId; }

            if (isStringNotEmpty(product.defaultCampaignId)) { this.defaultCampaignId = product.defaultCampaignId; }

            if (isListNotEmpty(product.variants)) { this.variants = product.variants; }

            if (isStringNotEmpty(product.instructions)) { this.instructions = product.instructions; }
            if (isStringNotEmpty(product.strength)) { this.strength = product.strength; }
            if (isStringNotEmpty(product.form)) { this.form = product.form; }
            if (isStringNotEmpty(product.route)) { this.route = product.route; }
            if (isBooleanTrue(product.isControlled)) { this.isControlled = true; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,

            visibleToCustomers: this.visibleToCustomers,
            categoryIds: this.categoryIds,

            attributes: this.attributes,
            description: this.description,
            dimensions: this.dimensions,
            weight: this.weight,

            featured: this.featured,
            imageRefIds: this.imageRefIds,
            metaData: this.metaData,
            regularPrice: this.regularPrice,
            salePrice: this.salePrice,
            compareAtPrice: this.compareAtPrice,
            purchasable: this.purchasable,
            sku: this.sku,
            shortSku: this.shortSku,
            productFamilyId: this.productFamilyId,
            offerGroupId: this.offerGroupId,
            commercialEquivalencyGroupId: this.commercialEquivalencyGroupId,
            formulationCode: this.formulationCode,
            activeIngredientIds: this.activeIngredientIds,
            additiveIds: this.additiveIds,
            soldIndividually: this.soldIndividually,
            status: this.status,
            stockStatus: this.stockStatus,
            stockQuantity: this.stockQuantity,
            lowStockAmount: this.lowStockAmount,
            taxStatus: this.taxStatus,
            tags: this.tags,
            productType: this.productType,
            displayOrder: this.displayOrder,
            isVirtual: this.isVirtual,
            isSale: this.isSale,
            manageStock: this.manageStock,
            brand: this.brand,
            gtin: this.gtin,
            msrp: this.msrp,
            shippingClassId: this.shippingClassId,
            shippingRequired: this.shippingRequired,

            duration: this.duration,

            requiresConfirmation: this.requiresConfirmation,
            requiresClinicianApproval: this.requiresClinicianApproval,
            requiresClinicianEncounter: this.requiresClinicianEncounter,
            requiresSurveys: this.requiresSurveys,

            requiredDocuments: this.requiredDocuments,

            surveys: this.surveys,

            conditionIds: this.conditionIds,
            biomarkers: this.biomarkers,

            stackedOptions: this.stackedOptions,
            stackEncounters: this.stackEncounters,
            stackShipments: this.stackShipments,

            relatedProductIds: this.relatedProductIds,
            crossSellProductIds: this.crossSellProductIds,

            carrier: this.carrier,
            carrierShippingMethod: this.carrierShippingMethod,
            shipRefrigerated: this.shipRefrigerated,

            subscriptionPrice: this.subscriptionPrice,
            subscriptionPeriod: this.subscriptionPeriod,
            subscriptionPeriodInterval: this.subscriptionPeriodInterval,
            subscriptionLength: this.subscriptionLength,
            subscriptionLimit: this.subscriptionLimit,
            subscriptionSyncRenewalsDate: this.subscriptionSyncRenewalsDate,
            subscriptionSyncRenewals: this.subscriptionSyncRenewals,
            subscriptionOneTimeShipping: this.subscriptionOneTimeShipping,
            subscriptionSignUpFee: this.subscriptionSignUpFee,

            labId: this.labId,
            waitTime: this.waitTime,
            hasAtHomeResults: this.hasAtHomeResults,
            specimenType: this.specimenType,

            panelIds: this.panelIds,
            biomarkerCount: this.biomarkerCount,
            collectionMethod: this.collectionMethod,
            requiresFasting: this.requiresFasting,
            fastingInstructions: this.fastingInstructions,
            testSchedule: this.testSchedule,

            monitoringPanelIds: this.monitoringPanelIds,

            acceptsInsurance: this.acceptsInsurance,
            productRules: this.productRules,
            addOnRules: this.addOnRules,

            count: this.count,
            countUnits: this.countUnits,
            packaging: this.packaging,

            detailSections: this.detailSections,

            bundleDiscounts: this.bundleDiscounts,

            bundleShipmentCount: this.bundleShipmentCount,

            clinicianEncounterType: this.clinicianEncounterType,
            clinicianEncounterFrequency: this.clinicianEncounterFrequency,
            clinicianEncounterPrice: this.clinicianEncounterPrice,
            ordersPerClinicianEncounter: this.ordersPerClinicianEncounter,

            productSchedules: this.productSchedules,

            practiceId: this.practiceId,
            pharmacyId: this.pharmacyId,

            defaultCampaignId: this.defaultCampaignId,

            variants: this.variants,

            instructions: this.instructions,
            strength: this.strength,
            form: this.form,
            route: this.route,
            isControlled: this.isControlled,
        };
    }

    getPrice = () => {
        if (this.productType.includes('SUBSCRIPTION') && isNumberGreaterThanZero(this.subscriptionPrice)) {
            return this.subscriptionPrice;
        } else if (isBooleanTrue(this.isSale) && isNumberGreaterThanZero(this.salePrice)) {
            return this.salePrice;
        } else {
            return this.regularPrice;
        }
    }

    getComparePrice = () => {
        let price = 0;
        let savings = 0;
        let savingsPercentage = 0;
        if (this.productType.includes('SUBSCRIPTION') && isNumberGreaterThanZero(this.subscriptionPrice)) {
            price = this.regularPrice;
            savings = this.regularPrice - this.subscriptionPrice;
            savingsPercentage = (savings / price) * 100;
        } else if (isBooleanTrue(this.isSale) && isNumberGreaterThanZero(this.salePrice)) {
            price = this.regularPrice;
            savings = this.regularPrice - this.salePrice;
            savingsPercentage = (savings / price) * 100;
        } else if (isNumberGreaterThanZero(this.msrp)) {
            price = this.msrp;
            savings = this.msrp - this.regularPrice;
            savingsPercentage = (savings / price) * 100;
        } else if (isNumberGreaterThanZero(this.compareAtPrice)) {
            price = this.compareAtPrice;
            savings = this.compareAtPrice - this.regularPrice;
            savingsPercentage = (savings / price) * 100;
        } else {
            price = this.regularPrice;
            savings = 0;
            savingsPercentage = 0;
        }

        return { price, savings, savingsPercentage };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        if (isNumberEmpty(this.regularPrice)) {
            throw new Error('Regular price is required.');
        }
        if (isStringEmpty(this.sku)) {
            throw new Error('SKU is required.');
        }
        validateUniqueSurveyFlowKeys(this.surveys, 'Product surveys');

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + Product.getFileExt(file);
                const storageRef = ref(storage, 'products/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);

        return toLower(ext);
    }

    static uploadImages = async (files) => {
        let fileRefIds = [];
        if (isListNotEmpty(files)) {
            for (const file of files) {
                const uploadRes = await Product.addFile(file);
                // console.log(uploadRes);
                if (isObjectNotEmpty(uploadRes)) {
                    fileRefIds.push(uploadRes.ref.fullPath);
                }
            }
        }

        return fileRefIds;
    }

    static addImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Product.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                return await addValueToChildArray(Product.collectionName, id, 'imageRefIds', uploadRes.ref.fullPath);
            }
        }

        return false;
    }

    create = async () => {
        if (this.isValid()) {
            this.imageRefIds = await Product.uploadImages(isListNotEmpty(this.#images) ? [...this.#images] : []);

            const newEntity = await create(Product.collectionName, this.toDto());
            if (isObjectNotEmpty(newEntity)) {
                if (isStringNotEmpty(newEntity.shippingClassId)) {
                    const shippingClass = await ShippingClass.getById(newEntity.shippingClassId);
                    if (isObjectNotEmpty(shippingClass)) {
                        const updatedProductIds = uniq([...shippingClass.productIds, newEntity.shippingClassId]);
                        await ShippingClass.updateField(shippingClass.id, 'productIds', updatedProductIds);
                    }
                }
            }

            return newEntity;
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Product.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedProduct, oldProduct) => {
        const updatedObj = new Product(updatedProduct);
        if (updatedObj.isValid() && id === updatedProduct.id) {
            if (isListNotEmpty(updatedObj.#images)) {
                const imageRefIds = await Product.uploadImages(updatedObj.#images);
                updatedObj.imageRefIds = isListNotEmpty(updatedObj.imageRefIds) ? [...updatedObj.imageRefIds, ...imageRefIds] : [...imageRefIds];
            }

            const success = await update(Product.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                if (isStringNotEmpty(updatedObj.shippingClassId)) {
                    const shippingClass = await ShippingClass.getById(updatedObj.shippingClassId);
                    if (isObjectNotEmpty(shippingClass)) {
                        const updatedProductIds = uniq([...shippingClass.productIds, updatedObj.shippingClassId]);
                        await ShippingClass.updateField(shippingClass.id, 'productIds', updatedProductIds);
                    }
                }

                return { ...oldProduct, ...updatedObj };
            }
        } else {
            throw new Error('id and id field of the updated product must match.')
        }

        return null;
    }

    static updateImages = async (id, images, oldProduct) => {
        let imageRefIds = [];
        if (isListNotEmpty(images)) {
            const orderedImages = lodashOrderBy(images, ['order'], ['asc']);
            // console.log('orderedImages', orderedImages);
            for (const image of orderedImages) {
                if (isNotNullOrUndefined(image.file)) {
                    const uploadRes = await Product.addFile(image.file);
                    // console.log(uploadRes);
                    if (isObjectNotEmpty(uploadRes)) {
                        imageRefIds.push(uploadRes.ref.fullPath);
                    }
                } else if (isStringNotEmpty(image.fileRefId)) {
                    imageRefIds.push(image.fileRefId);
                }
            }
        }
        // console.log('imageRefIds', imageRefIds);

        const success = await update(Product.collectionName, id, { imageRefIds: imageRefIds });
        if (isBooleanTrue(success)) {
            return { ...oldProduct, imageRefIds: imageRefIds };
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Product.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Product.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Product.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Product.collectionName, fieldName, fieldValue, comparer);
    }

    static getByCategoryId = async (categoryId) => {
        return await getBySingleQuery(Product.collectionName, 'categoryIds', categoryId, 'in');
    }

    static getByProductType = async (productType) => {
        return await getBySingleQuery(Product.collectionName, 'productType', productType, '==');
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Product.collectionName, ids, 'in');
    }

    static getByConditionId = async (conditionId) => {
        return await getBySingleQuery(Product.collectionName, 'conditionIds', conditionId, 'array-contains');
    }

    static getByLabId = async (labId) => {
        return await getBySingleQuery(Product.collectionName, 'labId', labId, '==');
    }

    static getBySku = async (sku) => {
        const list = await getBySingleQuery(Product.collectionName, 'sku', sku, '==');
        if (isListNotEmpty(list)) {
            return list[0];
        }

        return null;
    }

    static getByShortSku = async (shortSku) => {
        const list = await getBySingleQuery(Product.collectionName, 'shortSku', shortSku, '==');
        if (isListNotEmpty(list)) {
            return list[0];
        }

        return null;
    }

    static getCount = async (queryParameters) => {
        const { productType, categoryId, conditionId, visibleToCustomers, status, featured, purchasable, soldIndividually, isVirtual, isSale, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(productType)) {
            queryConstraints.push(where('productType', 'in', productType));
        } else if (isStringNotEmptyOrZeroLength(productType)) {
            queryConstraints.push(where('productType', '==', productType));
        }

        if (isListNotEmpty(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains-any', categoryId));
        } else if (isStringNotEmptyOrZeroLength(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains', categoryId));
        }

        if (isListNotEmpty(conditionId)) {
            queryConstraints.push(where('conditionIds', 'array-contains-any', conditionId));
        } else if (isStringNotEmptyOrZeroLength(conditionId)) {
            queryConstraints.push(where('conditionIds', 'array-contains', conditionId));
        }

        if (isBooleanTrue(visibleToCustomers)) {
            queryConstraints.push(where('visibleToCustomers', '==', true));
        } else if (isBooleanFalse(visibleToCustomers)) {
            queryConstraints.push(where('visibleToCustomers', '==', false));
        }

        if (isBooleanTrue(featured)) {
            queryConstraints.push(where('featured', '==', true));
        } else if (isBooleanFalse(featured)) {
            queryConstraints.push(where('featured', '==', false));
        }

        if (isBooleanTrue(purchasable)) {
            queryConstraints.push(where('purchasable', '==', true));
        } else if (isBooleanFalse(purchasable)) {
            queryConstraints.push(where('purchasable', '==', false));
        }

        if (isBooleanTrue(soldIndividually)) {
            queryConstraints.push(where('soldIndividually', '==', true));
        } else if (isBooleanFalse(soldIndividually)) {
            queryConstraints.push(where('soldIndividually', '==', false));
        }

        if (isBooleanTrue(isVirtual)) {
            queryConstraints.push(where('isVirtual', '==', true));
        } else if (isBooleanFalse(isVirtual)) {
            queryConstraints.push(where('isVirtual', '==', false));
        }

        if (isBooleanTrue(isSale)) {
            queryConstraints.push(where('isSale', '==', true));
        } else if (isBooleanFalse(isSale)) {
            queryConstraints.push(where('isSale', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Product.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'name') => {
        const { productType, categoryId, conditionId, visibleToCustomers, status, featured, purchasable, soldIndividually, isVirtual, isSale, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(status)) {
            queryConstraints.push(where('status', 'in', status));
        } else if (isStringNotEmptyOrZeroLength(status)) {
            queryConstraints.push(where('status', '==', status));
        }

        if (isListNotEmpty(productType)) {
            queryConstraints.push(where('productType', 'in', productType));
        } else if (isStringNotEmptyOrZeroLength(productType)) {
            queryConstraints.push(where('productType', '==', productType));
        }

        if (isListNotEmpty(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains-any', categoryId));
        } else if (isStringNotEmptyOrZeroLength(categoryId)) {
            queryConstraints.push(where('categoryIds', 'array-contains', categoryId));
        }

        if (isListNotEmpty(conditionId)) {
            queryConstraints.push(where('conditionIds', 'array-contains-any', conditionId));
        } else if (isStringNotEmptyOrZeroLength(conditionId)) {
            queryConstraints.push(where('conditionIds', 'array-contains', conditionId));
        }

        if (isBooleanTrue(visibleToCustomers)) {
            queryConstraints.push(where('visibleToCustomers', '==', true));
        } else if (isBooleanFalse(visibleToCustomers)) {
            queryConstraints.push(where('visibleToCustomers', '==', false));
        }

        if (isBooleanTrue(featured)) {
            queryConstraints.push(where('featured', '==', true));
        } else if (isBooleanFalse(featured)) {
            queryConstraints.push(where('featured', '==', false));
        }

        if (isBooleanTrue(purchasable)) {
            queryConstraints.push(where('purchasable', '==', true));
        } else if (isBooleanFalse(purchasable)) {
            queryConstraints.push(where('purchasable', '==', false));
        }

        if (isBooleanTrue(soldIndividually)) {
            queryConstraints.push(where('soldIndividually', '==', true));
        } else if (isBooleanFalse(soldIndividually)) {
            queryConstraints.push(where('soldIndividually', '==', false));
        }

        if (isBooleanTrue(isVirtual)) {
            queryConstraints.push(where('isVirtual', '==', true));
        } else if (isBooleanFalse(isVirtual)) {
            queryConstraints.push(where('isVirtual', '==', false));
        }

        if (isBooleanTrue(isSale)) {
            queryConstraints.push(where('isSale', '==', true));
        } else if (isBooleanFalse(isSale)) {
            queryConstraints.push(where('isSale', '==', false));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Product.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Product.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Product.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'name');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Product;
